import { useRef } from 'react';
import type { circlesSection } from '../../../../app/_shared/marketing/landingContent';
import { useScrollParallax } from '../../hooks/useScrollParallax';
import { AppScreenshot } from './AppScreenshot';
import {
  CIRCLE_INSIDE_SCREENSHOTS,
  CIRCLE_WRAP_SCREENSHOTS,
} from '../../assets/marketing/app-screenshots/appScreenshotsExport';
import './landingRef.css';

type Props = {
  groups: typeof circlesSection.shotGroups;
};

const SHOTS_BY_GROUP = {
  inside: CIRCLE_INSIDE_SCREENSHOTS,
  wrap: CIRCLE_WRAP_SCREENSHOTS,
} as const;

type ShotGroup = Props['groups'][number];

/**
 * Scroll depth for the two groups. Same direction, different rates — the second group lags the
 * scroll a little more than the first, so the two rows sit at different depths against the sky
 * rather than sliding past each other. Strength × travel (96px) is the peak offset: ~10px and
 * ~17px, clamped by maxPx. The hook is a no-op under prefers-reduced-motion.
 *
 * The row is a pin-free neighbour of the walkthrough (see the note on section tinting in
 * landingRef.css): the transform lives on `.ml-parallaxLift` INSIDE each group, never on the row
 * or the product-flow wrapper, so it creates no stacking context the fixed-frame pin can see.
 */
const GROUP_PARALLAX = {
  inside: { strength: 0.1, maxPx: 14 },
  wrap: { strength: 0.18, maxPx: 24 },
} as const;

function ScreenshotGroup({ group, groupIndex }: { group: ShotGroup; groupIndex: number }) {
  const scopeRef = useRef<HTMLElement>(null);
  const parallax = GROUP_PARALLAX[group.id];
  useScrollParallax(scopeRef, parallax.strength, { maxPx: parallax.maxPx });

  const shots = SHOTS_BY_GROUP[group.id];

  return (
    <section
      ref={scopeRef}
      className="circle-shots__group ml-parallaxScope"
      aria-labelledby={`circle-shots-${group.id}`}>
      <div className="ml-parallaxLift">
        <header className="circle-shots__groupHead">
          <p id={`circle-shots-${group.id}`} className="section-eyebrow ml-text-eyebrow">
            {group.label}
          </p>
          <p className="circle-shots__groupBody ml-text-body-secondary">{group.body}</p>
        </header>

        <div className="circle-shots__row">
          {shots.map((shot, shotIndex) => (
            <AppScreenshot
              key={shot.alt}
              shot={shot}
              treatment={groupIndex === 0 && shotIndex === 0 ? 'device' : 'moment'}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * The Circle walkthrough's real captures, in two labelled groups.
 *
 * The grouping is the point. These five have always been in a meaningful order — the prompt, a
 * reply, the side thread, then the private picks and the Mutual that opens — but presented as one
 * undifferentiated row they read as a pile of screenshots, with no way to tell where to start.
 * A heading per group and a single left-to-right reading direction is all the structure the
 * sequence needed.
 *
 * The very first capture keeps a device frame. It is the only one on the page, and it is what tells
 * a visitor this is a mobile app before the store badges do; the rest are cropped to the moment
 * that carries the claim, because a whole screen at row width renders the app's own copy far too
 * small to read.
 *
 * Everything loads lazily (ResponsiveImage defaults to `loading="lazy"` + `fetchPriority="low"`)
 * so it never contends with the hero image; each crop window shows a skeleton shimmer until its
 * WebP decodes (see `.mkt-shimmer` in landingRef.css).
 */
export function CircleScreenshotsRow({ groups }: Props) {
  return (
    <div className="circle-shots">
      {groups.map((group, groupIndex) =>
        SHOTS_BY_GROUP[group.id].length < 1 ? null : (
          <ScreenshotGroup key={group.id} group={group} groupIndex={groupIndex} />
        )
      )}
    </div>
  );
}
